import { useState, useEffect, useRef } from 'react';
import { 
    Grid, 
    Box, 
    TextField, 
    Button, 
    Typography, 
    InputAdornment,
    Alert,
    Snackbar,
    CircularProgress
} from '@mui/material';
import { 
    Person, 
    Email, 
    Phone, 
    Business,
    Message as MessageIcon,
    Send 
} from '@mui/icons-material';
import { submitContactForm } from '../../../services/api';
import { getSubmitErrorMessage } from '../../../services/utils';
import Turnstile from './Turnstile';

const Contact = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [touched, setTouched] = useState({});
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaFailed, setCaptchaFailed] = useState(false);
    const turnstileRef = useRef(null);

    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.2 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.company.trim()) newErrors.company = 'Company/Organization is required';
        if (!formData.message.trim()) newErrors.message = 'Message is required';
        if (!captchaToken) newErrors.captcha = 'Please wait for the security check to finish, then try again.';
        return newErrors;
    };

    const handleChange = (field) => (e) => {
        setFormData({ ...formData, [field]: e.target.value });
        if (touched[field]) {
            const newErrors = { ...errors };
            delete newErrors[field];
            setErrors(newErrors);
        }
    };

    const handleBlur = (field) => () => {
        setTouched({ ...touched, [field]: true });
        const newErrors = validateForm();
        if (newErrors[field]) {
            setErrors({ ...errors, [field]: newErrors[field] });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length === 0) {
            setSubmitting(true);
            try {
                setErrorMessage('');
                await submitContactForm({ ...formData, turnstileToken: captchaToken });
                setShowSuccess(true);
                setFormData({ name: '', email: '', company: '', phone: '', message: '' });
                setTouched({});
            } catch (err) {
                console.error('Contact submit failed:', err);
                setErrorMessage(getSubmitErrorMessage(err));
            } finally {
                setSubmitting(false);
                setCaptchaToken('');
                turnstileRef.current?.reset();
            }
        } else {
            setErrors(newErrors);
            setTouched({ name: true, email: true, company: true, message: true });
        }
    };

    return (
        <Box
            id="contact"
            ref={sectionRef}
            className={`clearbox-hero contact-hero ${isVisible ? "is-visible" : ""}`}
            sx={{
                position: "relative",
                background: "linear-gradient(135deg, #f5f7fa 0%, #edf1f7 100%)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        >
            <Box
                sx={{
                    maxWidth: "1400px",
                    margin: "0 auto",
                    padding: { xs: "80px 24px 60px", md: "100px 40px 80px" },
                }}
                className="clearbox-hero-inner"
            >
                <Grid
                    container
                    spacing={6}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {/* Left: Text Content */}
                    <Grid item xs={12} md={5}>
                        <Box
                            className="clearbox-hero-text"
                            sx={{
                                textAlign: { xs: "center", md: "left" },
                                color: "var(--secondary-color)",
                                pr: { md: 4 },
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    letterSpacing: "0.18em",
                                    color: "rgba(15,23,42,0.6)",
                                    fontWeight: 600,
                                    mb: 1.5,
                                }}
                            >
                                Contact Us
                            </Typography>

                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 800,
                                    color: "var(--secondary-color)",
                                    fontSize: { xs: "2rem", sm: "2.3rem", md: "2.6rem" },
                                    lineHeight: 1.1,
                                    mb: 2,
                                    letterSpacing: "-0.04em",
                                }}
                            >
                                Get In Touch
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: "1rem", md: "1.1rem" },
                                    maxWidth: { xs: "100%", md: "400px" },
                                    mx: { xs: "auto", md: 0 },
                                    lineHeight: 1.7,
                                    color: "rgba(15,23,42,0.7)",
                                }}
                            >
                                Have a question or want to work together? We'd love to hear from you. 
                                Fill out the form and we'll get back to you as soon as possible.
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Right: Form */}
                    <Grid item xs={12} md={7}>
                        <Box
                            className="clearbox-hero-image"
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                backgroundColor: "rgba(255,255,255,0.95)",
                                borderRadius: { xs: 3, md: 4 },
                                px: { xs: 3, sm: 4, md: 5 },
                                py: { xs: 4, sm: 5, md: 5 },
                                boxShadow: "inset 0 0 0 1px rgba(148,163,184,0.25)",
                                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                                "&:hover": {
                                    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12)",
                                },
                            }}
                        >
                            <Grid container spacing={3}>
                                {/* Name Field */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        value={formData.name}
                                        onChange={handleChange('name')}
                                        onBlur={handleBlur('name')}
                                        error={touched.name && !!errors.name}
                                        helperText={touched.name && errors.name}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person sx={{ color: 'var(--primary-color)' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                backgroundColor: '#f5f5f7',
                                                transition: 'all 0.3s ease',
                                                '& fieldset': {
                                                    borderColor: 'rgba(108, 152, 173, 0.2)',
                                                    borderWidth: '2px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: '#ffffff',
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'var(--secondary-color)',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: 'var(--primary-color)',
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Company Field */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Company/Organization"
                                        value={formData.company}
                                        onChange={handleChange('company')}
                                        onBlur={handleBlur('company')}
                                        error={touched.company && !!errors.company}
                                        helperText={touched.company && errors.company}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Business sx={{ color: 'var(--primary-color)' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                backgroundColor: '#f5f5f7',
                                                transition: 'all 0.3s ease',
                                                '& fieldset': {
                                                    borderColor: 'rgba(108, 152, 173, 0.2)',
                                                    borderWidth: '2px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: '#ffffff',
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'var(--secondary-color)',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: 'var(--primary-color)',
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Email Field */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange('email')}
                                        onBlur={handleBlur('email')}
                                        error={touched.email && !!errors.email}
                                        helperText={touched.email && errors.email}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email sx={{ color: 'var(--primary-color)' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                backgroundColor: '#f5f5f7',
                                                transition: 'all 0.3s ease',
                                                '& fieldset': {
                                                    borderColor: 'rgba(108, 152, 173, 0.2)',
                                                    borderWidth: '2px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: '#ffffff',
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'var(--secondary-color)',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: 'var(--primary-color)',
                                                }
                                            }
                                        }}
                                    />
                                </Grid>


                                {/* Phone Field */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number (Optional)"
                                        value={formData.phone}
                                        onChange={handleChange('phone')}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Phone sx={{ color: 'var(--primary-color)' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                backgroundColor: '#f5f5f7',
                                                transition: 'all 0.3s ease',
                                                '& fieldset': {
                                                    borderColor: 'rgba(108, 152, 173, 0.2)',
                                                    borderWidth: '2px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: '#ffffff',
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'var(--secondary-color)',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: 'var(--primary-color)',
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Message Field */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Your Message"
                                        multiline
                                        rows={4}
                                        value={formData.message}
                                        onChange={handleChange('message')}
                                        onBlur={handleBlur('message')}
                                        error={touched.message && !!errors.message}
                                        helperText={touched.message && errors.message}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                                                    <MessageIcon sx={{ color: 'var(--primary-color)' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                backgroundColor: '#f5f5f7',
                                                transition: 'all 0.3s ease',
                                                '& fieldset': {
                                                    borderColor: 'rgba(108, 152, 173, 0.2)',
                                                    borderWidth: '2px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'var(--primary-color)',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: '#ffffff',
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'var(--secondary-color)',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: 'var(--primary-color)',
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Captcha */}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
                                        <Turnstile
                                            ref={turnstileRef}
                                            onVerify={(token) => { setCaptchaToken(token); setCaptchaFailed(false); }}
                                            onExpire={() => setCaptchaToken('')}
                                            onError={() => setCaptchaFailed(true)}
                                        />
                                        {captchaFailed && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                The verification could not load. Please refresh the page or try again later.
                                            </Typography>
                                        )}
                                        {!captchaFailed && touched.message && errors.captcha && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                {errors.captcha}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, mt: 1 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={submitting}
                                            endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                            sx={{
                                                px: 4,
                                                py: 1.2,
                                                borderRadius: "999px",
                                                backgroundColor: "var(--primary-color)",
                                                fontWeight: 700,
                                                textTransform: "none",
                                                fontSize: "1rem",
                                                minWidth: "180px",
                                                boxShadow:
                                                    "0 10px 30px rgba(108, 152, 173, 0.6), 0 0 0 1px rgba(255,255,255,0.15)",
                                                "&:hover": {
                                                    backgroundColor: "#5a8499",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 14px 40px rgba(108, 152, 173, 0.7)",
                                                },
                                                "&:active": {
                                                    transform: "translateY(0)",
                                                },
                                                "&:disabled": {
                                                    backgroundColor: "rgba(108, 152, 173, 0.5)",
                                                }
                                            }}
                                        >
                                            {submitting ? 'Sending...' : 'Send Message'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Success Message */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={6000}
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setShowSuccess(false)} 
                    severity="success" 
                    sx={{ 
                        width: '100%',
                        borderRadius: '12px',
                        fontSize: '1rem'
                    }}
                >
                    Message sent successfully! We'll get back to you soon.
                </Alert>
            </Snackbar>

            {/* Error Message */}
            <Snackbar
                open={Boolean(errorMessage)}
                autoHideDuration={10000}
                onClose={() => setErrorMessage('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setErrorMessage('')} 
                    severity="error" 
                    sx={{ 
                        width: '100%',
                        borderRadius: '12px',
                        fontSize: '1rem'
                    }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Contact;